import { useMemo } from 'react';
import * as React from 'react';

import {
  BetStatus,
  MultiraceProgram,
  MultiraceType,
  RaceEntryStatus,
} from '@/lib/apollo/types/gen/graphql';

import RefundData from './RefundData';
import ResultData from './ResultData';
import {
  OtherData,
  OtherHeader,
  OtherInfoTableWrapper,
  Table,
  TableData,
  TableHeader,
  TableRow,
  TargetRaceData,
} from './style';

interface MultiraceTableProps {
  multiraceProgram: MultiraceProgram;
  displayChanceCount?: boolean;
}

const MultiraceTable: React.FC<MultiraceTableProps> = ({
  multiraceProgram,
  displayChanceCount,
}) => {
  const getRace = (raceNum: number) =>
    multiraceProgram.raceProgram.races.find(r => r.raceNum === raceNum)!;

  const getHitBetCount = (index: number) =>
    multiraceProgram.targetRaceHitBetCount[index];

  // 特払いが発生したかどうか
  const isSpecialPayment = multiraceProgram.multiraceRefunds.some(
    r => r.betStatus === BetStatus.SpecialPayment,
  );

  // 全対象レースが確定（中止を含む）したかどうか
  const isCompleted = multiraceProgram.raceProgram.races.every(
    r => r.raceEntryStatus !== RaceEntryStatus.Unregistered,
  );

  const { isCanceled } = multiraceProgram;

  const releaseBetCount = useMemo(
    () => multiraceProgram.releaseBetCount / multiraceProgram.unitOfRelease,
    [multiraceProgram.releaseBetCount, multiraceProgram.unitOfRelease],
  );

  const hitBetCount = useMemo(
    () => multiraceProgram.hitBetCount / multiraceProgram.unitOfRelease,
    [multiraceProgram.hitBetCount, multiraceProgram.unitOfRelease],
  );

  return (
    <>
      <Table>
        <tbody>
          <TableRow>
            <TableHeader>対象レース</TableHeader>
            {multiraceProgram.targetRaceNumbers.map(num => (
              <TargetRaceData key={`targetRace-${num}`}>{num}R</TargetRaceData>
            ))}
          </TableRow>
          <TableRow>
            <TableHeader>結果</TableHeader>
            {multiraceProgram.targetRaceNumbers.map(raceNum => (
              <ResultData
                key={`result-data-${raceNum}`}
                race={getRace(raceNum)}
                multiraceType={multiraceProgram.multiraceType}
                isCanceled={isCanceled}
              />
            ))}
          </TableRow>
          {displayChanceCount && (
            <TableRow>
              <TableHeader>
                残り
                <br />
                チャンス数
              </TableHeader>
              {multiraceProgram.targetRaceNumbers.map((raceNum, i) => (
                <TableData key={`chance-${raceNum}`}>
                  {getHitBetCount(i) === undefined ? '' : getHitBetCount(i)}
                </TableData>
              ))}
            </TableRow>
          )}
        </tbody>
      </Table>
      <OtherInfoTableWrapper>
        <Table>
          <tbody>
            {multiraceProgram.multiraceType !== MultiraceType.Chariloto3 && (
              <TableRow>
                <OtherHeader>前回キャリーオーバー額</OtherHeader>
                <OtherData>
                  {multiraceProgram.previousCarryoverPrice.toLocaleString()}円
                </OtherData>
              </TableRow>
            )}
            <TableRow>
              <OtherHeader>販売口数</OtherHeader>
              <OtherData>
                {isCanceled ? '-' : <>{releaseBetCount.toLocaleString()}口</>}
              </OtherData>
            </TableRow>
            <TableRow>
              <OtherHeader>販売金額</OtherHeader>
              <OtherData>
                {isCanceled ? (
                  '-'
                ) : (
                  <>
                    {(
                      releaseBetCount *
                      multiraceProgram.unitOfRelease *
                      100
                    ).toLocaleString()}
                    円
                  </>
                )}
              </OtherData>
            </TableRow>
          </tbody>
        </Table>
        <Table>
          <tbody>
            <TableRow>
              <OtherHeader>的中口数</OtherHeader>
              <OtherData>
                {isCanceled || !isCompleted ? (
                  '-'
                ) : (
                  <>{hitBetCount.toLocaleString()}口</>
                )}
              </OtherData>
            </TableRow>
            <TableRow>
              <OtherHeader>1口あたりの払戻金額</OtherHeader>
              <RefundData
                isCanceled={isCanceled}
                isCompleted={isCompleted}
                isSpecialPayment={isSpecialPayment}
                unitOfRelease={multiraceProgram.unitOfRelease}
                multiraceRefunds={multiraceProgram.multiraceRefunds}
                multiraceType={multiraceProgram.multiraceType}
              />
            </TableRow>
            {multiraceProgram.multiraceType !== MultiraceType.Chariloto3 && (
              <TableRow>
                <OtherHeader>次回キャリーオーバー額</OtherHeader>
                <OtherData>
                  {isCompleted ? (
                    <>{multiraceProgram.carryoverPrice.toLocaleString()}円</>
                  ) : (
                    '-'
                  )}
                </OtherData>
              </TableRow>
            )}
          </tbody>
        </Table>
      </OtherInfoTableWrapper>
    </>
  );
};

export default MultiraceTable;
